import axios from "axios"

const API_URL = process.env.REACT_APP_APP_URL

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Add a request interceptor to include the token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("CC_Token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// User Service
export const userService = {
  register: (userData) => api.post("/register", userData),
  login: (credentials) => api.post("/login", credentials),
  me: () => api.get("/me"),
  sendResetLink: (email) => api.post("/password/email", { email }),
  resetPassword: (data) => api.post("/password/reset", data),
  listInstructors: () => api.get("/users/instructor"),
  getMembers: () => api.get("/users/members"),
  getAvailableMembersToAssign: () => api.get("/users/getAvailableMembersToAssign"),
  assignCoach: (data) => api.patch("/users/assignCoach", data),
  removeCoach: (data) => api.patch("/users/removeCoach", data),
  assignRole: (id, role) => api.post(`/admin/assign-role/${id}`, { role }),
  activateUser: (id) => api.patch(`/admin/activate-user/${id}`),
  deactivateUser: (id) => api.patch(`/admin/deactivate-user/${id}`),
  listNonActiveUsers: () => api.get("/admin/non-active-users"),
}

// Invoice Service
export const invoiceService = {
  getAllInvoices: () => api.get("/invoices"),
  getTotalIncome: () => api.get("/invoices/total"),
  getInvoicesByUserId: (userId) => api.get(`/invoices/user/${userId}`),
  getInvoiceById: (id) => api.get(`/invoices/${id}`),
  filterAndSortInvoices: (params) => api.get("/invoices/filter", { params }),
  createInvoice: (invoiceData) => api.post("/invoices", invoiceData),
  updateInvoice: (id, invoiceData) => api.put(`/invoices/${id}`, invoiceData),
  deleteInvoice: (id) => api.delete(`/invoices/${id}`),
  getInvoicesByUsername: (username) => api.get(`/invoices/username/${username}`),
  getInvoiceItemsByInvoiceNumber: (invoiceNumber) => api.get(`/invoice-items/${invoiceNumber}`),
  createInvoiceItem: (itemData) => api.post("/invoice-items", itemData),
  getUserInvoices: (userId) => api.get(`/user-invoices/${userId}`),
  updateInvoiceItemPrice: (id, price) => api.put(`/invoice-items/${id}/price`, { price }),
  updateInvoiceItemPaymentStatus: (data) => api.put("/invoice-items/payment-status", data),
  getAllInvoiceItems: () => api.get("/all-invoices"),
}

// Lesson Service
export const lessonService = {
  getAllLessons: () => api.get("/lessons"),
  getLessonById: (id) => api.get(`/lessons/${id}`),
  createLesson: (lessonData) => api.post("/lessons", lessonData),
  updateLesson: (id, lessonData) => api.put(`/lessons/${id}`, lessonData),
  deleteLesson: (id) => api.delete(`/lessons/${id}`),
  filterAndSortLessons: (params) => api.get("/lessons/filter", { params }),
}

// Notification Service
export const notificationService = {
  getAllNotifications: () => api.get("/notifications"),
  getNotificationsByUser: () => api.get("/notifications/byUser"),
  markAsRead: (id) => api.put(`/notifications/${id}/mark-as-read`),
  listByUserId: (userId) => api.get(`/notifications/user/${userId}`),
  storeByUser: (data) => api.post("/notifications/byUser", data),
  createNotification: (notificationData) => api.post("/notifications", notificationData),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
}

// Seance Service
export const seanceService = {
  getAllSeances: () => api.get("/seances"),
  getSeanceById: (id) => api.get(`/seances/${id}`),
  createSeance: (seanceData) => api.post("/seances", seanceData),
  updateSeance: (id, seanceData) => api.put(`/seances/${id}`, seanceData),
  deleteSeance: (id) => api.delete(`/seances/${id}`),
  getCount: () => api.get("/seances/count"),
  activateCavalier: (id) => api.patch(`/admin/activate-cavalier/${id}`),
  deactivateCavalier: (id) => api.patch(`/admin/deactivate-cavalier/${id}`),
}

// Stock Service
export const stockService = {
  getAllStocks: () => api.get("/stock"),
  getStockById: (id) => api.get(`/stock/${id}`),
  createStock: (stockData) => api.post("/stock", stockData),
  updateStock: (id, stockData) => api.put(`/stock/${id}`, stockData),
  deleteStock: (id) => api.delete(`/stock/${id}`),
}

// Banner Service
export const bannerService = {
  getAllBanners: () => api.get("/banners"),
  createBanner: (bannerData) => api.post("/banners", bannerData),
  deleteBanner: (id) => api.delete(`/banners/${id}`),
}

// Horse Service
export const horseService = {
  getAllHorses: () => api.get("/api/horses"),
  getHorseOwners: () => api.get("/api/horse-owners"),
  getHorse: (id) => api.get(`/api/horses/${id}`),
  createHorse: (horseData) => api.post("/api/horses", horseData),
  updateHorse: (id, horseData) => api.put(`/api/horses/${id}`, horseData),
  deleteHorse: (id) => api.delete(`/api/horses/${id}`),
  getHorseWeightHistory: (horseId) => api.get(`/api/horses/${horseId}/weight-history`),
  addHorseWeight: (horseId, weightData) => api.post(`/api/horses/${horseId}/weight`, weightData),
  deleteHorseWeight: (weightId) => api.delete(`/api/horse-weights/${weightId}`),
  addBeddingHistory: (horseId, beddingData) => api.post(`/api/horses/${horseId}/bedding-history`, beddingData),
  addFoodSupplementHistory: (horseId, supplementData) =>
    api.post(`/api/horses/${horseId}/food-supplement-history`, supplementData),
}

// Box Service (NEW)
export const boxService = {
  getAllBoxes: () => api.get("/api/boxes"),
  updateBox: (name, boxData) => api.put(`/api/boxes/${name}`, boxData),
  deleteBox: (name) => api.delete(`/api/boxes/${name}`),
}

export default api
